import { FC, useEffect, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { DragEndEvent, DndContext, closestCenter } from "@dnd-kit/core";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, FormControl, FormHelperText, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import { useUploadAttachment } from "src/Pages/Company/Absences/api/employee.queries";
import { Dropzone } from "@benefit-systems/hr-tools-ui";
import SortableFileItem from "src/Pages/Company/Absences/Forms/SortableFileItem/SortableFileItem";

interface FileItem {
  id: string;
  file: File;
  isUploading: boolean;
  isValid: boolean;
  errorMessage?: string;
  size?: string;
}

interface IAbsenceRequestAttachmentsProps {
  attachments?: {
    attachmentId: string;
    fileName: string;
  }[];
}

const MAX_FILES = 5;

const AbsenceRequestAttachments: FC<IAbsenceRequestAttachmentsProps> = ({
  attachments = [],
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { append, remove, replace } = useFieldArray({
    control,
    name: "attachments",
  });
  const { mutateAsync: uploadFile } = useUploadAttachment();

  const [localFiles, setLocalFiles] = useState<FileItem[]>([]);

  useEffect(() => {
    if (localFiles.length === 0 && attachments.length > 0) {
      const mappedFiles = attachments.map((attachment) => ({
        id: attachment.attachmentId,
        file: new File([], attachment.fileName),
        isUploading: false,
        isValid: true,
      }));

      setLocalFiles(mappedFiles);
      replace(mappedFiles.map((item) => ({ id: item.id })));
    }
  }, [attachments, localFiles.length, replace]);

  const handleFileChange = async (file: File, isValid: boolean) => {
    const localId = uuidv4();

    const newFile = {
      id: localId,
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      extension: file.name.split(".").pop() || "",
      mimeType: file.type,
      fileLink: "",
      isUploading: isValid,
      isValid,
      errorMessage: isValid ? "" : "Invalid file",
      file,
    };

    setLocalFiles((prev) => [...prev, newFile]);

    if (isValid) {
      try {
        const response = await uploadFile(file);
        const { attachmentId } = response;

        setLocalFiles((prevItems) =>
          prevItems.map((item) =>
            item.id === localId
              ? { ...item, id: attachmentId, isUploading: false, isValid: true }
              : item
          )
        );

        append({ id: attachmentId });
      } catch {
        setLocalFiles((prevItems) =>
          prevItems.map((item) =>
            item.id === localId
              ? {
                  ...item,
                  isUploading: false,
                  isValid: false,
                  errorMessage: __("Błąd wysyłania pliku."),
                }
              : item
          )
        );
      }
    }
  };

  const handleRemoveFile = (id: string, index: number) => {
    setLocalFiles((prevItems) => prevItems.filter((item) => item.id !== id));
    remove(index);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = localFiles.findIndex((item) => item.id === active.id);
      const newIndex = localFiles.findIndex((item) => item.id === over.id);

      const reorderedFiles = arrayMove(localFiles, oldIndex, newIndex);

      setLocalFiles(reorderedFiles);
      replace(reorderedFiles.map((item) => ({ id: item.id })));
    }
  };

  return (
    <Controller
      control={control}
      name="attachments"
      render={() => (
        <FormControl
          fullWidth
          margin="normal"
          error={!!errors.attachments}
          data-testid="absence-request-form-field__attachments"
        >
          <Box>
            <Typography variant="body2">
              {__("Pliki")}
              {` ${localFiles.length}/${MAX_FILES}`}
            </Typography>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[
                restrictToVerticalAxis,
                restrictToWindowEdges,
                restrictToParentElement,
              ]}
            >
              <SortableContext
                items={localFiles.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Box
                  sx={{
                    position: "relative",
                    margin: "16px 0",
                    padding: "0 16px",
                    display: "flex",
                    flexDirection: "column",
                    rowGap: "16px",
                  }}
                >
                  {localFiles.map((item, index) => (
                    <SortableFileItem
                      key={item.id}
                      id={item.id}
                      file={item.file}
                      isUploading={item.isUploading}
                      isValid={item.isValid}
                      index={index}
                      errorMessage={item.errorMessage}
                      onRemove={handleRemoveFile}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>

            <Dropzone
              onFilesAccepted={(files) => {
                files.forEach(({ file, isValid }) =>
                  handleFileChange(file, isValid)
                );
              }}
              maxFiles={MAX_FILES}
              allowMultiple
              maxFileSize={5}
              acceptTypes={[
                { type: "application/pdf", label: "PDF" },
                { type: "image/jpeg", label: "JPG" },
                { type: "image/png", label: "PNG" },
                { type: "application/msword", label: "DOC" },
                {
                  type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  label: "DOCX",
                },
              ]}
              activeLinkText="Dodaj załącznik / załączniki z dysku"
              translateFunction={__}
              showInvalidFiles
            />
          </Box>
          {errors.attachments?.message && (
            <FormHelperText>
              {String(errors.attachments.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default AbsenceRequestAttachments;
